class VariantImageSelector {
  constructor() {
    this.previousVariant = null;
    this.init();
  }

  init() {
    // Initialize on page load
    this.updateDropdownSwatches();
    this.attachEventListeners();
    this.setupImageSwatchInteractions();
    this.initializeVariantImages();
  }

  initializeVariantImages() {
    // Mark all slides with "variant-only" in alt text
    const productMedia = document.querySelector('.product-media-container');
    if (!productMedia) return;

    const slides = productMedia.querySelectorAll('.swiper-slide');
    slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img && img.alt) {
        const altLower = img.alt.toLowerCase();
        if (altLower.includes('variant-only')) {
          // Extract variant name from alt text (before |variant-only)
          const variantName = img.alt.split('|')[0].trim().toLowerCase();
          slide.setAttribute('data-variant-image', variantName);
        }
      }
    });

    // Show images for current variant on page load
    this.updateVariantImages();

    // Ensure we're not starting on a hidden slide - with retry mechanism
    this.waitForSwiperAndInit();
  }

  waitForSwiperAndInit(attempts = 0) {
    const carousel = document.querySelector('product-carousel');

    if (carousel && carousel.swiperInstance) {
      const swiper = carousel.swiperInstance;
      const activeSlide = swiper.slides[swiper.activeIndex];

      if (activeSlide && activeSlide.hasAttribute('data-variant-image') && !activeSlide.classList.contains('show-variant-image')) {
        // Navigate to first visible slide
        const nextVisibleIndex = this.findNextVisibleSlide(swiper, 'next');
        if (nextVisibleIndex !== -1) {
          swiper.slideTo(nextVisibleIndex, 0);
        }
      }

      // Now update swiper with our handlers
      this.updateSwiperSlides();
    } else if (attempts < 20) {
      // Retry up to 20 times (2 seconds total)
      setTimeout(() => {
        this.waitForSwiperAndInit(attempts + 1);
      }, 100);
    }
  }

  updateVariantImages() {
    const productMedia = document.querySelector('.product-media-container');
    if (!productMedia) return;

    // Get current variant option1 (Flavour)
    const variantSelect = document.querySelector('select[name="options[Flavour]"], select[name="options[1]"]');
    const variantRadio = document.querySelector('input[name="options[Flavour]"]:checked, input[name="options[1]"]:checked');

    let currentFlavour = '';
    if (variantSelect) {
      currentFlavour = variantSelect.value.toLowerCase();
    } else if (variantRadio) {
      currentFlavour = variantRadio.value.toLowerCase();
    }

    // Show/hide variant images
    const variantSlides = productMedia.querySelectorAll('.swiper-slide[data-variant-image]');

    variantSlides.forEach(slide => {
      const slideVariant = slide.getAttribute('data-variant-image');

      // If no variant is selected (empty currentFlavour), hide all variant images
      let shouldShow = false;
      if (currentFlavour && currentFlavour.trim() !== '') {
        // Match if exact match OR if one contains the other
        shouldShow = slideVariant === currentFlavour ||
                     currentFlavour.includes(slideVariant) ||
                     slideVariant.includes(currentFlavour);
      }

      if (shouldShow) {
        slide.classList.add('show-variant-image');
      } else {
        slide.classList.remove('show-variant-image');
      }
    });

    // Update Swiper to skip hidden variant images
    this.updateSwiperSlides();
  }

  updateSwiperSlides() {
    const carousel = document.querySelector('product-carousel');
    if (!carousel || !carousel.swiperInstance) {
      return;
    }

    const swiper = carousel.swiperInstance;

    // Completely remove hidden variant slides from Swiper
    swiper.slides.forEach(slide => {
      const hasVariantAttr = slide.hasAttribute('data-variant-image');
      const hasShowClass = slide.classList.contains('show-variant-image');

      if (hasVariantAttr && !hasShowClass) {
        // Disable this slide completely
        slide.classList.add('swiper-slide-disabled');
        slide.style.pointerEvents = 'none';
        slide.style.opacity = '0';
        slide.style.position = 'absolute';
        slide.style.left = '-9999px';
      } else {
        slide.classList.remove('swiper-slide-disabled');
        slide.style.pointerEvents = '';
        slide.style.opacity = '';
        slide.style.position = '';
        slide.style.left = '';
      }
    });

    // Use Swiper's allowSlideNext/allowSlidePrev to control navigation
    swiper.allowSlideNext = true;
    swiper.allowSlidePrev = true;

    // Intercept Swiper's navigation using events
    swiper.off('slideNextTransitionStart');
    swiper.off('slidePrevTransitionStart');

    swiper.on('slideNextTransitionStart', () => {
      this.checkAndSkipHiddenSlide(swiper, 'next');
    });

    swiper.on('slidePrevTransitionStart', () => {
      this.checkAndSkipHiddenSlide(swiper, 'prev');
    });

    // Also check current slide
    this.checkAndSkipHiddenSlide(swiper, 'next');

    swiper.update();
  }

  checkAndSkipHiddenSlide(swiper, direction) {
    const currentSlide = swiper.slides[swiper.activeIndex];

    if (!currentSlide) return;

    const hasVariantAttr = currentSlide.hasAttribute('data-variant-image');
    const hasShowClass = currentSlide.classList.contains('show-variant-image');
    const isHidden = hasVariantAttr && !hasShowClass;

    if (isHidden) {
      const nextIndex = this.findNextVisibleSlide(swiper, direction);

      if (nextIndex !== -1 && nextIndex !== swiper.activeIndex) {
        // Use setTimeout to avoid conflicts with ongoing transitions
        setTimeout(() => {
          swiper.slideTo(nextIndex, 0);
        }, 0);
      }
    }
  }

  setupArrowHandlers(carousel, swiper) {
    const prevArrow = carousel.querySelector('[slide-prev]');
    const nextArrow = carousel.querySelector('[slide-next]');

    if (prevArrow) {
      // Remove old handler if exists
      if (this.handlePrevClick) {
        prevArrow.removeEventListener('click', this.handlePrevClick, true);
      }

      this.handlePrevClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        let targetIndex = swiper.activeIndex - 1;

        // Find previous visible slide
        while (targetIndex >= 0) {
          const slide = swiper.slides[targetIndex];
          if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
            swiper.slideTo(targetIndex);
            return;
          }
          targetIndex--;
        }
      };
      // Use capture phase to intercept before Swiper
      prevArrow.addEventListener('click', this.handlePrevClick, true);
    }

    if (nextArrow) {
      // Remove old handler if exists
      if (this.handleNextClick) {
        nextArrow.removeEventListener('click', this.handleNextClick, true);
      }

      this.handleNextClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        let targetIndex = swiper.activeIndex + 1;

        // Find next visible slide
        while (targetIndex < swiper.slides.length) {
          const slide = swiper.slides[targetIndex];
          if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
            swiper.slideTo(targetIndex);
            return;
          }
          targetIndex++;
        }
      };
      // Use capture phase to intercept before Swiper
      nextArrow.addEventListener('click', this.handleNextClick, true);
    }
  }

  handleSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];

    // Check if current slide is a hidden variant image
    if (activeSlide && activeSlide.hasAttribute('data-variant-image') && !activeSlide.classList.contains('show-variant-image')) {
      // Find next visible slide
      const direction = swiper.swipeDirection || 'next';
      let nextVisibleIndex = this.findNextVisibleSlide(swiper, direction);

      if (nextVisibleIndex !== -1 && nextVisibleIndex !== swiper.activeIndex) {
        swiper.slideTo(nextVisibleIndex, 0); // 0 = instant transition
      }
    }
  }

  findNextVisibleSlide(swiper, direction) {
    const currentIndex = swiper.activeIndex;
    const totalSlides = swiper.slides.length;

    // Try to find next visible slide
    if (direction === 'prev') {
      for (let i = currentIndex - 1; i >= 0; i--) {
        const slide = swiper.slides[i];
        if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
          return i;
        }
      }
    } else {
      for (let i = currentIndex + 1; i < totalSlides; i++) {
        const slide = swiper.slides[i];
        if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
          return i;
        }
      }
    }

    // If no visible slide found in that direction, try the other direction
    if (direction === 'next') {
      for (let i = currentIndex - 1; i >= 0; i--) {
        const slide = swiper.slides[i];
        if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
          return i;
        }
      }
    } else {
      for (let i = currentIndex + 1; i < totalSlides; i++) {
        const slide = swiper.slides[i];
        if (!slide.hasAttribute('data-variant-image') || slide.classList.contains('show-variant-image')) {
          return i;
        }
      }
    }

    return -1;
  }

  attachEventListeners() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.updateDropdownSwatches();
      this.updateProductMedia(event.detail?.variant);
      this.updateVariantImages();
    });

    // Listen for dropdown changes
    const dropdowns = document.querySelectorAll('.variant-pills-wrapper select, .select-wrapper select');
    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (event) => {
        this.updateSelectedDropdownSwatch(event.target);
        this.handleVariantImageChange(event.target);
        this.updateVariantImages();
      });
    });

    // Listen for swatch/pill clicks
    const variantInputs = document.querySelectorAll('input[name*="options["]');
    variantInputs.forEach(input => {
      input.addEventListener('change', (event) => {
        this.handleVariantImageChange(event.target);
        this.updateVariantImages();
      });
    });
  }

  setupImageSwatchInteractions() {
    // Add hover effects for image swatches
    const imageSwatches = document.querySelectorAll('.variant-image-swatch');
    imageSwatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', (e) => {
        const variantImage = e.target.getAttribute('data-variant-image');
        if (variantImage) {
          this.previewVariantImage(variantImage);
        }
      });

      swatch.addEventListener('mouseleave', () => {
        this.resetImagePreview();
      });
    });
  }

  handleVariantImageChange(input) {
    // Disable all variant image changes from inputs - only allow flavour changes via variant:change event
    return;
  }

  updateMainProductImage(imageUrl) {
    // Update the main product image
    const mainImage = document.querySelector('.product-main-slide-container .swiper-slide img');
    if (mainImage) {
      mainImage.src = imageUrl;
    }

    // Update carousel if present
    const carousel = document.querySelector('product-carousel');
    if (carousel) {
      this.updateCarouselToImage(imageUrl);
    }
  }

  previewVariantImage(imageUrl) {
    // Temporarily show the variant image on hover
    const mainImage = document.querySelector('.product-main-slide-container .swiper-slide img');
    if (mainImage) {
      mainImage.setAttribute('data-original-src', mainImage.src);
      mainImage.src = imageUrl;
    }
  }

  resetImagePreview() {
    // Reset to original image after hover
    const mainImage = document.querySelector('.product-main-slide-container .swiper-slide img');
    if (mainImage) {
      const originalSrc = mainImage.getAttribute('data-original-src');
      if (originalSrc) {
        mainImage.src = originalSrc;
        mainImage.removeAttribute('data-original-src');
      }
    }
  }

  updateCarouselToImage(imageUrl) {
    // Find the slide with matching image and navigate to it
    const slides = document.querySelectorAll('.swiper-slide img');
    let targetSlideIndex = 0;

    slides.forEach((img, index) => {
      if (img.src.includes(this.getImageId(imageUrl))) {
        targetSlideIndex = index;
      }
    });

    // Navigate carousel to the target slide
    const carousel = document.querySelector('product-carousel');
    if (carousel && carousel.swiperInstance) {
      carousel.swiperInstance.slideTo(targetSlideIndex);
    }
  }

  updateDropdownSwatches() {
    // Get the current product data
    const productData = this.getProductData();
    if (!productData) return;

    // Update each dropdown with variant images
    const dropdowns = document.querySelectorAll('.product-variant__option__item select');
    dropdowns.forEach(dropdown => {
      this.enhanceDropdownWithImages(dropdown, productData);
    });
  }

  enhanceDropdownWithImages(dropdown, productData) {
    const optionName = dropdown.name.match(/options\[(.*?)\]/)?.[1];
    if (!optionName) return;

    // Get the swatch display element if it exists
    const swatchDisplay = dropdown.closest('.select-wrapper')?.querySelector('[data-selected-dropdown-swatch]');
    if (!swatchDisplay) return;

    // Update the displayed swatch based on selected value
    this.updateSelectedDropdownSwatch(dropdown);
  }

  updateSelectedDropdownSwatch(dropdown) {
    const selectedValue = dropdown.value;
    const productData = this.getProductData();
    if (!productData) return;

    // Find variant with this option value
    const variant = productData.variants.find(v => {
      return v.options.includes(selectedValue);
    });

    if (variant && variant.featured_image) {
      const swatchDisplay = dropdown.closest('.select-wrapper')?.querySelector('[data-selected-dropdown-swatch]');
      if (swatchDisplay) {
        swatchDisplay.innerHTML = `
          <img 
            src="${this.getSizedImageUrl(variant.featured_image.src, '50x50')}"
            alt="${selectedValue}"
            width="30"
            height="30"
            style="object-fit: cover; border-radius: 3px;"
          >
        `;
        swatchDisplay.classList.add('has-variant-image');
      }
    }
  }

  updateProductMedia(variant) {
    if (!variant || !variant.featured_image) {
      return;
    }

    // Check if Flavour option changed (not Size)
    const shouldUpdateImage = this.shouldUpdateImageForVariant(variant);

    if (!shouldUpdateImage) {
      this.previousVariant = variant;
      return;
    }

    // Update main product media
    const productMedia = document.querySelector('.product-media-container');
    if (productMedia) {
      // Find media element with matching image
      const mediaElements = productMedia.querySelectorAll('[data-media-id]');
      mediaElements.forEach(media => {
        const mediaId = media.getAttribute('data-media-id');
        if (mediaId === variant.featured_image.id.toString()) {
          // Update carousel to show this media
          this.updateCarouselToMediaId(mediaId);
        }
      });
    }

    this.previousVariant = variant;
  }

  shouldUpdateImageForVariant(variant) {
    if (!this.previousVariant) {
      return true; // First load, show image
    }

    // Get variant options
    const previousOptions = this.previousVariant.options || [];
    const currentOptions = variant.options || [];

    // Check if only Size changed (assuming Size is not in option1)
    // Typically: option1 = Flavour, option2 = Size
    // Only update image if option1 (Flavour) changed
    if (previousOptions[0] !== currentOptions[0]) {
      return true; // Flavour changed, update image
    }

    return false; // Only Size changed, don't update image
  }

  updateCarouselToMediaId(mediaId) {
    const carousel = document.querySelector('product-carousel');
    if (carousel) {
      const slideToMedia = carousel.getAttribute('slide-to-media');
      if (slideToMedia !== mediaId) {
        carousel.setAttribute('slide-to-media', mediaId);
        // Trigger carousel update if method exists
        if (carousel.updateActiveSlide) {
          carousel.updateActiveSlide();
        }
      }
    }
  }

  getProductData() {
    // Try to get product data from the page
    const scriptTag = document.querySelector('script[type="application/json"]:not([id*="ProductJSON"])');
    if (scriptTag) {
      try {
        return JSON.parse(scriptTag.textContent);
      } catch (e) {
        console.error('Failed to parse product data:', e);
      }
    }
    return null;
  }

  getImageId(imageUrl) {
    // Extract image ID from Shopify image URL
    const matches = imageUrl.match(/\/(\d+)_/);
    return matches ? matches[1] : '';
  }

  getSizedImageUrl(src, size) {
    // Convert Shopify image URL to specific size
    if (!src) return '';
    
    // Remove any existing size parameter
    const baseUrl = src.split('?')[0].replace(/_\d+x\d+/, '');
    
    // Add new size
    const extension = baseUrl.split('.').pop();
    const urlWithoutExtension = baseUrl.substring(0, baseUrl.lastIndexOf('.'));
    
    return `${urlWithoutExtension}_${size}.${extension}`;
  }
}

// Enhanced variant change event handling
document.addEventListener('DOMContentLoaded', function() {
  // Initialize variant image selector
  new VariantImageSelector();

  // Enhanced variant dropdown functionality
  const variantDropdowns = document.querySelectorAll('variant-dropdown');
  variantDropdowns.forEach(dropdown => {
    const selects = dropdown.querySelectorAll('select');
    selects.forEach(select => {
      select.addEventListener('change', function() {
        // Update swatch display with variant image
        const option = this.options[this.selectedIndex];
        const variantImage = option.getAttribute('data-variant-image');
        
        if (variantImage) {
          const swatchDisplay = this.closest('.select-wrapper')?.querySelector('[data-selected-dropdown-swatch]');
          if (swatchDisplay) {
            swatchDisplay.style.backgroundImage = `url(${variantImage})`;
            swatchDisplay.style.backgroundSize = 'cover';
            swatchDisplay.style.backgroundPosition = 'center';
            swatchDisplay.classList.add('has-variant-image');
          }
        }
      });
    });
  });
});

// CSS for enhanced variant image functionality
const variantImageStyles = document.createElement('style');
variantImageStyles.textContent = `
  /* Enhanced variant image swatch styles */
  .variant-image-swatch {
    position: relative;
    display: inline-block;
    margin: 4px;
    transition: all 0.2s ease;
  }

  .variant-image-swatch::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: inherit;
    transition: border-color 0.2s ease;
  }

  .variant-image-swatch:hover::after {
    border-color: rgba(var(--color-foreground), 0.5);
  }

  input:checked + .variant-image-swatch::after {
    border-color: rgba(var(--color-foreground), 1);
    box-shadow: 0 0 0 1px rgba(var(--color-foreground), 1);
  }

  input:disabled + .variant-image-swatch {
    opacity: 0.5;
    cursor: not-allowed;
  }

  input:disabled + .variant-image-swatch:hover::after {
    border-color: transparent;
  }

  /* Dropdown swatch with variant image */
  .dropdown-swatch.has-variant-image {
    border: 1px solid rgba(var(--color-foreground), 0.2);
  }

  /* Pills with images */
  .pill-with-image img {
    transition: opacity 0.2s ease;
  }

  input:disabled + .pill-with-image img {
    opacity: 0.5;
  }

  /* Loading state for images */
  .variant-image-loading {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
  }

  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;
document.head.appendChild(variantImageStyles);


